import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { bundleIn, runningBundle, isNewVersion, mayAsk, readAsked, dayOf, ASK_KEY, CHECK_EVERY_MS, MIN_GAP_MS } from '@/lib/versionWatch';

/**
 * Asks an open tab to reload once a newer portal is live (lib/versionWatch.js says how it knows). It ASKS, never
 * reloads on its own: a reload takes away a half-written message or form. The ask stays until it is answered.
 * Checks every few minutes and whenever the tab comes back into view, so a tab left open overnight hears about
 * the deploy the moment someone looks at it. Silent on a dev server and on any failed check. At most once a day
 * (lib/versionWatch.js mayAsk): a day with ten deploys asks once.
 */
export function VersionWatch() {
  const shown = useRef(false);
  const last = useRef(0);
  useEffect(() => {
    const running = runningBundle(document);
    if (!running) return undefined;               // dev server: nothing to compare
    let gone = false;
    const check = async () => {
      if (gone || shown.current || Date.now() - last.current < MIN_GAP_MS) return;
      last.current = Date.now();
      try {
        const r = await fetch(`/?vcheck=${Date.now()}`, { cache: 'no-store', credentials: 'omit' });
        if (!r.ok) return;
        const served = bundleIn(await r.text());
        if (gone || !isNewVersion(running, served)) return;
        let last = null;
        try { last = readAsked(window.localStorage.getItem(ASK_KEY)); } catch { /* storage refused: ask */ }
        if (!mayAsk(last, served, Date.now())) return;     // already asked today about an earlier build: tomorrow
        try { window.localStorage.setItem(ASK_KEY, JSON.stringify({ day: dayOf(Date.now()), bundle: served })); } catch { /* fine */ }
        shown.current = true;
        toast('A new version of the portal is out', {
          id: 'portal-new-version',
          description: 'Reload to get it. Anything half-typed? Send or save it first.',
          duration: Infinity,
          action: { label: 'Reload', onClick: () => window.location.reload() },
          // the site's primary button (teal, black ink), so the one thing to press reads as the thing to press
          actionButtonStyle: { background: '#2bb3a8', color: '#0a0a0a', fontWeight: 600 },
        });
      } catch { /* offline or the server is mid-deploy: the next check will tell */ }
    };
    const onLook = () => { if (document.visibilityState === 'visible') check(); };
    const iv = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onLook);
    window.addEventListener('focus', onLook);
    return () => { gone = true; clearInterval(iv); document.removeEventListener('visibilitychange', onLook); window.removeEventListener('focus', onLook); };
  }, []);
  return null;
}
